/**
 * Pack 13.10 · Grounding UI toolkit.
 *
 * Consumes the read-only `_grounding` envelope shipped by Pack 13.9 on
 * `GET /api/leads/{id}`. Every component gracefully renders NOTHING when
 * the envelope is null / empty — legacy reports (Phase <13.8) simply
 * degrade to the classic layout.
 *
 * All components are pure presentational and reuse the existing Shadcn
 * primitives (Card, Button, Accordion) so we inherit the report page's
 * navy/accent theme without touching global styles.
 */
import React, { useMemo, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import {
  BadgeCheck,
  ShieldAlert,
  ScrollText,
  Activity,
  BookOpen,
  ExternalLink,
  Clock,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------
export function hasGrounding(g) {
  if (!g || typeof g !== "object") return false;
  const anyCitations = (g.citations || []).length > 0;
  const anyCards = (g.recommendation_cards || []).length > 0;
  const hasSummary = !!g.confidence_summary;
  return anyCitations || anyCards || hasSummary;
}

function _formatConfidence(c) {
  if (typeof c !== "number") return "—";
  return `${Math.round(c * 100)}%`;
}

function _formatDelta(delta) {
  if (typeof delta !== "number") return null;
  const sign = delta > 0 ? "+" : "";
  return `${sign}${Math.round(delta)}%`;
}

function _band(band) {
  switch ((band || "").toUpperCase()) {
    case "RED":
      return "bg-red-500/15 text-red-300 border-red-500/40";
    case "AMBER":
      return "bg-amber-500/15 text-amber-300 border-amber-500/40";
    case "GREEN":
      return "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
    default:
      return "bg-secondary text-muted-foreground border-border";
  }
}

function _priority(p) {
  switch ((p || "").toUpperCase()) {
    case "P0":
      return "bg-red-500/15 text-red-300 border-red-500/40";
    case "P1":
      return "bg-amber-500/15 text-amber-300 border-amber-500/40";
    default:
      return "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
  }
}

// ---------------------------------------------------------------------------
// Citation Pill — attaches next to a numeric claim
// ---------------------------------------------------------------------------
export function CitationPill({ citation }) {
  const [open, setOpen] = useState(false);
  if (!citation) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid={`citation-pill-${citation.id}`}
          className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-accent hover:bg-accent/20 hover:border-accent/60 transition-colors align-middle ml-1"
          aria-label={`Show evidence ${citation.id}`}
        >
          <BadgeCheck className="w-3 h-3" />
          {citation.id}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid={`citation-dialog-${citation.id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BadgeCheck className="w-4 h-4 text-accent" />
            Evidence {citation.id}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {citation.kpi_code}
            {citation.firmographic_band ? ` · ${citation.firmographic_band}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="text-sm space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <StatRow label="Prospect" value={citation?.value?.prospect} unit={citation.unit} />
            <StatRow label="Peer P50" value={citation?.value?.p50} unit={citation.unit} />
            <StatRow label="Peer P25" value={citation?.value?.p25} unit={citation.unit} />
            <StatRow label="Peer P75" value={citation?.value?.p75} unit={citation.unit} />
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${_band(citation.band)}`}>
              {citation.band || "—"}
            </span>
            {citation.direction && (
              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/40 px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                {citation.direction}
              </span>
            )}
            {typeof citation.delta_pct_vs_p50 === "number" && (
              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/40 px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                Δ vs P50: {_formatDelta(citation.delta_pct_vs_p50)}
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/40 px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
              Confidence {_formatConfidence(citation.confidence)}
            </span>
          </div>
          {citation.impact_hint && (
            <p className="text-xs text-muted-foreground leading-relaxed pt-1">
              {citation.impact_hint}
            </p>
          )}
          {citation.atom_source_id && (
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest pt-2 border-t border-border/60">
              Source · {citation.atom_source_id}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatRow({ label, value, unit }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-sans-ui font-semibold text-sm mt-1">
        {value === undefined || value === null ? "—" : `${value}${unit || ""}`}
      </div>
    </div>
  );
}

/**
 * Renders a paragraph of text with [E<n>] / [R<n>] tokens replaced by
 * clickable Citation pills where a match exists in `citationsMap`.
 * Unknown tokens are left in place as plain text — the validator already
 * stripped anything it couldn't prove.
 */
export function GroundedText({ text, citationsMap }) {
  if (!text) return null;
  if (!citationsMap || Object.keys(citationsMap).length === 0) return <>{text}</>;
  const parts = String(text).split(/(\[E\d+\]|\[R\d+\])/g);
  return (
    <>
      {parts.map((part, i) => {
        const m = /^\[(E\d+|R\d+)\]$/.exec(part);
        if (!m) return <React.Fragment key={i}>{part}</React.Fragment>;
        const token = m[1];
        const cit = citationsMap[token];
        if (cit && token.startsWith("E")) {
          return <CitationPill key={i} citation={cit} />;
        }
        // Recommendation tokens keep the raw text — the RecommendationCards
        // section already covers them, no dialog needed inline.
        return (
          <span
            key={i}
            className="inline-flex items-center rounded-full border border-border/60 bg-secondary/40 px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground ml-1"
            data-testid={`grounded-token-${token}`}
          >
            {token}
          </span>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Confidence Summary card
// ---------------------------------------------------------------------------
export function ConfidenceSummaryCard({ summary }) {
  if (!summary) return null;
  const items = [
    { key: "mean_confidence", label: "Overall confidence", value: _formatConfidence(summary.mean_confidence) },
    { key: "citations_count", label: "Evidence", value: summary.citations_count ?? 0 },
    { key: "recommendations_count", label: "Recommendations", value: summary.recommendations_count ?? 0 },
    { key: "regulatory_rules_count", label: "Regulatory", value: summary.regulatory_rules_count ?? 0 },
    { key: "validator_drops", label: "Validator drops", value: summary.validator_drops ?? 0 },
  ];
  return (
    <Card
      className="card-elevated p-6 border border-accent/20"
      data-testid="grounding-confidence-summary"
    >
      <div className="flex items-center gap-2 overline text-accent">
        <Activity className="w-3.5 h-3.5" /> Report Confidence
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
        {items.map((it) => (
          <div key={it.key} data-testid={`confidence-${it.key}`}>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {it.label}
            </div>
            <div className="font-display text-2xl mt-1 text-foreground">{it.value}</div>
          </div>
        ))}
      </div>
      {(summary.low_confidence || summary.fallback_used) && (
        <div
          className="mt-4 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200"
          data-testid="confidence-warning"
        >
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {summary.fallback_used
              ? "One or more grounding stages fell back to safe defaults; recommendations remain valid but citation coverage may be lower than usual."
              : "Overall confidence is below the trust threshold — treat numeric claims as directional and validate before quoting externally."}
          </span>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Recommendation Cards
// ---------------------------------------------------------------------------
export function RecommendationCards({ cards }) {
  if (!cards || cards.length === 0) return null;
  // Split so regulatory obligations don't dilute the strategic cards list.
  const strategic = cards.filter((c) => (c?.source_type || "").toLowerCase() !== "regulatory");
  if (strategic.length === 0) return null;
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="grounding-recommendation-cards">
      {strategic.map((card) => (
        <RecommendationCard key={card.id} card={card} />
      ))}
    </div>
  );
}

function RecommendationCard({ card }) {
  return (
    <Card
      className="card-elevated p-5 border border-accent/20"
      data-testid={`recommendation-card-${card.id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <span
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${_priority(card.priority)}`}
        >
          {card.priority || "—"}
        </span>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
          {card.id} · {card.source_type || "library"}
        </span>
      </div>
      <div className="mt-3 font-sans-ui font-semibold">{card.title || "(untitled)"}</div>
      {card.body && (
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{card.body}</p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {card.timeline && (
          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/40 px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
            <Clock className="w-3 h-3" /> {card.timeline}
          </span>
        )}
        {typeof card.confidence === "number" && (
          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/40 px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
            Confidence {_formatConfidence(card.confidence)}
          </span>
        )}
        {card.roi_estimate?.metric && (
          <span className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-accent">
            {card.roi_estimate.metric} {card.roi_estimate.low_pct}%–{card.roi_estimate.high_pct}%
            {card.roi_estimate.horizon_months ? ` / ${card.roi_estimate.horizon_months}mo` : ""}
          </span>
        )}
      </div>
      {(card.evidence_refs || []).length > 0 && (
        <div className="mt-4 pt-3 border-t border-border/60 text-[10px] text-muted-foreground">
          <span className="uppercase tracking-widest">Backed by</span>{" "}
          <span className="font-mono">{card.evidence_refs.join(", ")}</span>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Regulatory Obligations panel
// ---------------------------------------------------------------------------
export function RegulatoryObligationsPanel({ obligations }) {
  if (!obligations || obligations.length === 0) return null;
  return (
    <Card
      className="card-elevated p-6 border border-red-500/20"
      data-testid="grounding-regulatory-panel"
    >
      <div className="flex items-center gap-2 overline text-red-300">
        <ShieldAlert className="w-3.5 h-3.5" /> Regulatory Obligations
      </div>
      <div className="mt-4 space-y-3">
        {obligations.map((o) => (
          <div
            key={o.id || o.rule_id}
            data-testid={`regulatory-obligation-${o.rule_id || o.id}`}
            className="rounded-lg border border-border/60 bg-secondary/30 p-4"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-red-300">
                  {o.jurisdiction || "—"}
                </span>
                <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${_priority(o.priority)}`}>
                  {o.priority || "—"}
                </span>
              </div>
              {o.timeline && (
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  <Clock className="w-3 h-3 inline mr-1" /> {o.timeline}
                </span>
              )}
            </div>
            <div className="mt-3 font-sans-ui font-semibold">{o.title || o.rule_id}</div>
            {o.body && (
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{o.body}</p>
            )}
            {(o.supporting_kpis || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {o.supporting_kpis.map((k) => (
                  <span
                    key={k}
                    className="inline-flex items-center rounded-full border border-border/60 bg-background px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground font-mono"
                  >
                    {k}
                  </span>
                ))}
              </div>
            )}
            {o.source?.url && (
              <a
                href={o.source.url}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-accent hover:underline"
                data-testid={`regulatory-source-${o.rule_id || o.id}`}
              >
                <ExternalLink className="w-3 h-3" /> {o.source.publisher || o.source.url}
              </a>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Sources section — expandable list of every evidence citation used
// ---------------------------------------------------------------------------
export function SourcesSection({ citations, pipelineVersion, promptFingerprint }) {
  if (!citations || citations.length === 0) return null;
  return (
    <Card className="card-elevated p-6" data-testid="grounding-sources">
      <div className="flex items-center gap-2 overline">
        <BookOpen className="w-3.5 h-3.5" /> Sources & Evidence
      </div>
      <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
        Every numeric claim in this report is backed by one of the evidence records
        below. Expand any row to see the peer distribution.
      </p>
      <Accordion type="multiple" className="mt-4">
        {citations.map((c) => (
          <AccordionItem key={c.id} value={c.id} data-testid={`sources-item-${c.id}`}>
            <AccordionTrigger className="text-sm">
              <span className="flex items-center gap-3">
                <span className="font-mono text-accent">{c.id}</span>
                <span>{c.kpi_code}</span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${_band(c.band)}`}
                >
                  {c.band || "—"}
                </span>
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <StatRow label="Prospect" value={c?.value?.prospect} unit={c.unit} />
                <StatRow label="Peer P25" value={c?.value?.p25} unit={c.unit} />
                <StatRow label="Peer P50" value={c?.value?.p50} unit={c.unit} />
                <StatRow label="Peer P75" value={c?.value?.p75} unit={c.unit} />
              </div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-widest mt-3">
                {c.atom_source_id ? `Atom · ${c.atom_source_id}` : ""}
                {typeof c.confidence === "number" ? ` · Confidence ${_formatConfidence(c.confidence)}` : ""}
              </p>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      {(pipelineVersion || promptFingerprint) && (
        <p className="text-[10px] text-muted-foreground uppercase tracking-widest mt-4 pt-3 border-t border-border/60">
          <ScrollText className="w-3 h-3 inline mr-1" />
          Pipeline {pipelineVersion || "?"}
          {promptFingerprint ? ` · Fingerprint ${promptFingerprint.slice(0, 12)}` : ""}
        </p>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Convenience: map from token → citation, memoised at the caller.
// ---------------------------------------------------------------------------
export function useCitationsMap(grounding) {
  return useMemo(() => {
    if (!grounding) return {};
    const map = {};
    (grounding.citations || []).forEach((c) => {
      if (c?.id) map[c.id] = c;
    });
    return map;
  }, [grounding]);
}
